import React, {
  useContext,
  useCallback,
  useState,
  useLayoutEffect,
  useEffect,
} from "react";
import { Footer } from "./footer";
import { Gdpr, GDPR_COOKIE } from "./gdpr";
import { useInView } from "react-intersection-observer";
import "intersection-observer";
import { SiteContext } from "./site-context";
import { analytics } from "../../lib/analytics";
import clientConfig from "../../../client-config";
import useMedia from "use-media";
import { pageDocPath } from "../../../helpers";
import { cn } from "../../lib/helpers";
import {
  clientAttribution,
  appendUtms,
  attributionCookie,
  COOKIE_NAME,
} from "../../lib/client-attribution";
import { useCookies } from "react-cookie";
import { getCookieConsentValue } from "react-cookie-consent";
import { includePromos, StickyPopup } from "../sticky-popup";
import Container from "../global/container";
import { SearchUX } from "../sections/snowflake/search";
import { APP_AUTH_COOKIE } from "../../lib/score";
import { GlobalNav } from "./global-nav";
import { getBanners } from "../sections/snowflake/banner";
import { ClientContext } from "./client-context";
import { detectGdprRegion } from "../../lib/geo";
import * as tailwind from "../../../tailwind.config";

import * as globalStyles from "./global.module.css";
import * as styles from "./layout.module.css";

const isLiveProd = process.env.CONTEXT === "production";

const BACKGROUND_COLOR_MAP = new Map([
  ["darkGray", "bg-dark-85"],
  ["black", "bg-gray-1000"],
  ["gradientLavender", globalStyles.gradientLavender],
  ["purple", "bg-green-ultralight"],
  ["lightLavender", "bg-green-ultralight"],
  ["lightGreen", "bg-green-ultralight"],
  ["gridBlack", globalStyles.fixedGridBlack],
]);

export const DARK_BACKGROUNDS = ["darkGray", "black", "blackGlow", "transparentGlow", "innerDark", "gradientDark", "gridBlack", "gray-900"];
export const MISSING_UI_STRING = "Missing UI string";

export const Layout = ({
  children,
  site,
  hideNav,
  doc = {},
  isHomepage,
  assets,
  location,
  // page type only
  pageContext,
  darkBg
}) => {
  const [_cookies, setCookie] = useCookies([COOKIE_NAME]);
  const { ref, inView } = useInView({ initialInView: true });

  // filters
  const [menuMatch, setMenuMatch] = useState(null);
  const [filters, setFilters] = useState([]);
  // search
  const [isSearchActive, setSearchActive] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [query, setQuery] = useState("");
  const [searchTypes, setSearchTypes] = useState([]);

  const isBrowser = typeof window !== "undefined";

  const isActive = useCallback(
    (flag) => {
      const localOverride = isBrowser && localStorage.getItem(flag) === "true";
      return localOverride || (site.featureFlags && site.featureFlags.includes(flag));
    },
    [site, isBrowser]
  );

  const cookieValue = getCookieConsentValue(GDPR_COOKIE);
  const initialTrackingLevel = cookieValue === "true" ? "standard"
    : cookieValue === "essentials" ? "essentials"
      : cookieValue === "declined" ? "declined"
        : "essentials";
  const [trackingLevel, setTrackingLevel] = useState(initialTrackingLevel);

  const canTrack = isLiveProd || isActive("allowTrack");
  const shouldTrack = canTrack && trackingLevel === "standard";
  const shouldTrackEssentials = canTrack && trackingLevel === "essentials";

  useEffect(() => {
    if (!shouldTrack) {
      analytics.plugins.disable("google-tag-manager");
      return;
    }
    const enable = () => analytics.plugins.enable("google-tag-manager");
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(enable, { timeout: 3000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(enable, 3000);
    return () => clearTimeout(id);
  }, [shouldTrack]);

  useEffect(() => {
    const portalId = clientConfig.hubspot?.portalId;
    if (!shouldTrackEssentials || !portalId) return;
    if (document.getElementById("hs-script-loader")) return;
    const script = document.createElement("script");
    script.id = "hs-script-loader";
    script.src = `//js.hs-scripts.com/${portalId}.js`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [shouldTrack]);

  useEffect(() => {
    if (shouldTrackEssentials) {
      analytics.plugins.enable("google-analytics");
    } else {
      analytics.plugins.disable("google-analytics");
    }
  }, [shouldTrackEssentials]);

  useEffect(() => {
    if (cookieValue) return; // already consented — geo doesn't matter
    detectGdprRegion().then((isGdpr) => {
      setTrackingLevel(isGdpr ? "essentials" : "standard");
    });
  }, []);

  const logEvent = ({ label, category = "default", action = "log" }) => {
    analytics.track(category, {
      category,
      action,
      label,
      instanceName: "code-events",
    });
  };

  const siteContext = useContext(SiteContext);
  const token = useCallback(
    (s) => {
      const search = site.interfaceTokens.find((t) => t.label === s);
      return search
        ? search.value
        : siteContext.uiTokens.has(s)
          ? siteContext.uiTokens.get(s)
          : MISSING_UI_STRING;
    },
    [site, siteContext]
  );

  // change to useCallback or put in useEffect with state
  const [cookies, _setCookie] = useCookies([APP_AUTH_COOKIE]);
  const isPopupDemo =
    isActive("popupDemo") &&
    site?.calendly &&
    (!isActive("popupAuthExclude") || !cookies.hasOwnProperty("cr-token"));

  const isLandingPage =
    doc?.contentType === "landing" ||
    ["company", "prospect", "competitor"].includes(doc?._type) ||
    location?.pathname === "/hello";

  const isBlog = !!pageDocPath(doc).match(
    /^\/blog\/*$|\/blog\/archive\/*$|^\/topics/
  );

  const isDarkBg = darkBg || DARK_BACKGROUNDS.includes(
    doc?.backgroundColor
  );

  const background = BACKGROUND_COLOR_MAP.get(doc.backgroundColor);

  const isHeaderPage =
    ["header", "headerLogos"].includes(doc?.sections?.[0]?.name) ||
    ["category", "signal"].includes(doc?._type);

  const domain = isBrowser
    ? window.location.hostname.replace(/^www\./i, "")
    : "commonroom.io";

  useLayoutEffect(() => {
    // UTM query param handling and storage
    clientAttribution({
      isActive,
      storage: analytics.storage,
      referrer: document.referrer,
      callback: (urlParams, storage) => {
        // add params to links to contact and signup
        appendUtms({
          dom: document,
          doc,
          urlParams,
          storage: analytics.storage,
        });
        // regenerate attribution cookie, delay for localStorage
        if (isBrowser) {
          setTimeout(() => {
            setCookie(COOKIE_NAME, attributionCookie(storage), {
              path: "/",
              domain: `.${domain}`,
            });
          }, 0);
        }
      },
    });
  }, []);

  const [banners] = useState(getBanners({ doc, assets }) || []);
  const isAdmin = !!analytics.storage.getItem("show");

  // Scroll-based background translation for gridBlack
  const [backgroundTranslateY, setBackgroundTranslateY] = useState(0);

  useEffect(() => {
    if (doc?.backgroundColor !== "gridBlack" || !isBrowser) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;

      // Calculate scroll progress (0 to 1)
      const scrollProgress = Math.min(Math.max(scrollY / documentHeight, 0), 1);

      // Map to smaller translation (upward movement)
      const translateY = scrollProgress * -80;
      setBackgroundTranslateY(translateY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, [doc?.backgroundColor, isBrowser]);

  return (
    <SiteContext.Provider
      value={{
        ...site,
        calendly: site.calendly,
        banners,
        isPageTop: inView,
        setTrackingLevel,
        hideNav: hideNav || isLandingPage,
        metrics: {
          analytics,
          logEvent,
          logClick: (action, label) => {
            logEvent({ label, category: "click", action });
          },
          logError: (label, obj) => {
            logEvent({ category: "error", label });
            console.error(label, obj || null);
          },
        },
        token,
        isActive,
        isAdmin,
        // @todo PageContext
        doc,
        isHomepage,
        isBlog,
        isNotMobile: useMedia({ minWidth: tailwind.theme.screens.md }),
        isLarge: useMedia({ minWidth: tailwind.theme.screens.xl }),
        isBrowser,
        isDarkBg,
        isHeaderPage,
        generateUnpublishedPages: process.env.GATSBY_GENERATE_UNPUBLISHED_PAGES,
        location,
        isLiveProd,
        buildToken: process.env.GATSBY_BUILD_TOKEN,
      }}
    >
      <div
        className={cn(
          "relative",
          background,
          doc?.backgroundColor === "purple" ? "overflow-hidden" : null
        )}
        style={{
          minHeight: "100vh",
          ...(doc?.backgroundColor === "gridBlack" && {
            backgroundPosition: `center, top center`,
            backgroundPositionY: `center, ${backgroundTranslateY}px`,
          }),
        }}
      >
        <ClientContext.Provider
          value={{
            filters,
            setFilters,
            menuMatch,
            setMenuMatch,
            isSearchActive,
            setSearchActive,
            setQuery,
            query,
            searchTypes,
            setSearchTypes,
            hasSearched,
            setHasSearched,
            // @todo move into client content
            // isPageTop: inView,
            // setIsTracking,
            // isBrowser,
            // isNotMobile: useMedia({ minWidth: tailwind.theme.screens.md }),
            // isLarge: useMedia({ minWidth: tailwind.theme.screens.xl }),
            // isAdmin: !!analytics.storage.getItem("show"),
          }}
        >
          {doc?.backgroundColor === "black" && (
            <Container maxWidth="max-w-screen-lg" className="relative">
              <div
                className={cn(
                  "absolute top-0 right-0 bottom-0 left-0 h-144",
                  globalStyles.radialPurple
                )}
              ></div>
            </Container>
          )}
          <div style={{ height: 0 }} ref={ref}></div>
          <GlobalNav />
          <div
            id="content"
            className={cn(
              styles.root,
              // doc?._type === "page" && !isHeaderPage && includePromos(doc)
              //   ? banners.length > 0
              //     ? "pt-24 md:pt-30"
              //     :
              //     !isHomepage
              //       ? "pt-12 md:pt-14"
              //       : null
              //   : null
            )}
          >
            {children}
          </div>
          {isPopupDemo && <StickyPopup />}
          <Footer />
          {isSearchActive ? <SearchUX /> : null}
          <Gdpr />
          {isAdmin && <AdminLink doc={doc} />}
        </ClientContext.Provider>
      </div>
    </SiteContext.Provider>
  );
};

const SANITY_URL_PREFIX = "https://www-common-room-studio.netlify.app/desk/";
const TYPE_MAP = {
  post: "blogPosts",
  asset: "guidesAssetsBanners",
  page: "pages",
  playbook: "playbooks",
  video: "videosPodcasts",
  offering: "offeringPagesProductSolution",
  story: "orderable-story",
  signal: "signalGuidesOld",
  signalSource: "orderable-signalSource",
  scenario: "signalScenariosNew",
  prompt: "prompts",
  documentation: "orderable-documentation",
  asset: "assetsPdfGuidesBanners",
  category: "orderable-category",
  prospect: "prospectsAbm",
  competitor: "competitors",
  integration: "orderable-integration",
  agent: "agent",
  author: "profilesAuthorSpeaker",
  invite: "eventInvites",
  training: "orderable-training",
};

export const AdminLink = ({ doc, noOverlay, className }) => (
  <a
    href={
      doc._type === "video"
        ? "https://docs.google.com/spreadsheets/d/1yWkgYLsQ4pEqYs_S0eSSzI6YdgiYlr1v_e3g_WlYhyo/edit"
        : `${SANITY_URL_PREFIX}${TYPE_MAP[doc._type]};${doc._id}`
    }
    className={cn(
      "text-link font-bold bg-white p-0.5 px-1.5 text-xs border border-green-emerald hover:bg-green-emerald hover:text-white rounded-md",
      noOverlay ? null : "absolute top-3 right-3 z-100",
      className
    )}
    target="_blank"
  >
    Edit
  </a>
);
